import React, { useState } from "react";
import {
  Layout,
  Card,
  TextField,
  TextStyle,
  ResourceList,
  ResourceItem,
  Banner,
  Button,
  TextContainer,
} from "@shopify/polaris";
import { useShop } from "providers/ShopProvider";
import { useToast } from "providers/ToastProvider";
import {
  addMultiSiteNetworkShop,
  removeMultiSiteNetworkShops,
} from "services/wpApi";

export default function MultiSiteNetwork() {
  const { shop, setShop } = useShop();
  const setToast = useToast();
  const [newStoreName, setNewStoreName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [selectedShops, setSelectedShops] = useState([]);

  const multiSiteNetwork = (shop && shop.settings && shop.settings.multiSiteNetwork) || [];
  const hasPermission = shop && shop.permissions && shop.permissions.includes("multi_site_network");

  const handleAdd = async () => {
    if (!newStoreName) return;
    setIsLoading(true);
    setError(false);

    try {
      const { data } = await addMultiSiteNetworkShop(newStoreName);
      setShop({
        ...shop,
        settings: { ...shop.settings, multiSiteNetwork: data.multiSiteNetwork },
      });
      setToast("Added " + newStoreName + " to your multi-store network");
      setNewStoreName("");
    } catch (e) {
      var message = "Unable to add this store. Make sure Real ID is installed and has an Enterprise subscription.";
      if (e.response && e.response.data && e.response.data.error) {
        message = e.response.data.error;
      }
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemove = async () => {
    if (selectedShops.length === 0) return;
    setIsLoading(true);

    try {
      const { data } = await removeMultiSiteNetworkShops(selectedShops);
      setShop({
        ...shop,
        settings: { ...shop.settings, multiSiteNetwork: data.multiSiteNetwork },
      });
      setSelectedShops([]);
      setToast("Removed stores from your network");
    } catch (e) {
      var message = "Unable to update your network, please contact support.";
      if (e.response && e.response.data && e.response.data.error) {
        message = e.response.data.error;
      }
      setToast(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout.AnnotatedSection
      title="Multi-Site Network"
      description={
        <TextContainer>
          <p>Add your other Shopify or WooCommerce sites to your network.</p>
          <p>
            Other sites in your multi-site network will share the same passed ID
            checks, so customers already verified on these stores will not be
            required to verify their ID again on this store or other stores in
            the network.
          </p>
        </TextContainer>
      }
    >
      <Card
        title="In-network stores"
        primaryFooterAction={{
          content: "Add store",
          disabled: isLoading || !newStoreName,
          loading: isLoading,
          onAction: handleAdd,
        }}
      >
        <Card.Section>
          <div style={{ marginBottom: "16px" }}>
            <Banner title="Note" status="info">
              <p style={{ marginBottom: "8px" }}>
                All stores in your network must have Real ID installed and
                have an <strong>Enterprise</strong> plan subscription.
              </p>
              <p>
                <a href="mailto:support@getverdict.com">Contact us</a> to set
                up Enterprise plans on your network of stores.
              </p>
            </Banner>
          </div>
          <div style={{ marginTop: "16px", marginBottom: "16px" }}>
            <TextField
              autoComplete="off"
              disabled={isLoading || !hasPermission}
              label="Store Name"
              helpText="Enter your store's domain (e.g. store-name.myshopify.com or your WooCommerce site's domain)"
              error={error}
              value={newStoreName}
              onChange={setNewStoreName}
            />
          </div>
        </Card.Section>

        <ResourceList
          resourceName={{ plural: "Stores", singular: "Store" }}
          idForItem={function (item) { return item; }}
          items={multiSiteNetwork.filter(Boolean)}
          selectedItems={selectedShops}
          onSelectionChange={function (selectedItems) {
            if (selectedItems === "All" || selectedItems[0] === "0") {
              setSelectedShops(multiSiteNetwork);
              return;
            }
            setSelectedShops(selectedItems);
          }}
          promotedBulkActions={[
            {
              content: "Remove from network",
              destructive: true,
              disabled: isLoading || selectedShops.length === 0,
              onAction: handleRemove,
            },
          ]}
          renderItem={function (shopName) {
            return (
              <ResourceItem
                id={shopName}
                accessibilityLabel={"View details for " + shopName}
                onClick={function () {}}
                persistActions
              >
                <h3>
                  <TextStyle variation="strong">{shopName}</TextStyle>
                </h3>
              </ResourceItem>
            );
          }}
        />
      </Card>
    </Layout.AnnotatedSection>
  );
}
